import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  InputSize,
  InputText,
  Select,
  SelectSize,
  SelectType,
  Textarea,
} from '@ui/src';
import AdditionalInfoBlock from '../../../individual/aml/additional-info-block';
import { GroupOfCompaniesTypes } from './types';
import { CustomerStatus } from '../../../../hoc/customers/types';
import RegulatedCompanyRow from './row';

import cn from './styles.module.css';

const GroupOfCompanies = ({
  control,
  clearErrors,
  setValue,
  status,
  errors,
  fields,
  countries,
  sourceOfWealth,
  baseCurrency,
}: GroupOfCompaniesTypes) => (
  <AdditionalInfoBlock title="Group of companies">
    <>
      <RegulatedCompanyRow title="Does the Customer belong to any group of companies?">
        <Controller
          control={control}
          name="belongsToGroup"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={[
                {
                  name: 'Yes',
                  value: 'yes',
                },
                {
                  name: 'No',
                  value: 'no',
                },
              ]}
              value={value as string}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['belongsToGroup']);

                if (e.target.value === 'no') {
                  clearErrors([
                    'parentCompanyName',
                    'parentCompanyCountryId',
                    'subsidiariesInfo',
                    'mainActivities',
                    'groupWealthSourceIds',
                    'estimatedTotalWealthEuro',
                  ]);

                  setValue('parentCompanyName', '');
                  setValue('parentCompanyCountryId', '');
                  setValue('subsidiariesInfo', '');
                  setValue('mainActivities', '');
                  setValue('groupWealthSourceIds', []);
                  setValue('estimatedTotalWealthEuro', '');
                }
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.belongsToGroup?.message}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>
      {fields?.belongsToGroup === 'yes' && (
        <>
          <RegulatedCompanyRow title="Provide the name of the parent or managing company">
            <Controller
              control={control}
              name="parentCompanyName"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter name..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['parentCompanyName']);
                  }}
                  className="borderless"
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.parentCompanyName?.message}
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Country of incorporation of the parent or managing company">
            <Controller
              control={control}
              name="parentCompanyCountryId"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Autocomplete
                  name={name}
                  placeholder="Select country..."
                  list={countries}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['parentCompanyCountryId']);
                  }}
                  selectWrapperClassName={
                    status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
                  }
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.parentCompanyCountryId?.message}
                  fullWidth
                  borderless
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Subsidiary companies and associate companies of the group and countries of their incorporation">
            <Controller
              control={control}
              name="subsidiariesInfo"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Textarea
                  name={name}
                  title=""
                  placeholder="Enter description..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['subsidiariesInfo']);
                  }}
                  className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                  error={errors?.subsidiariesInfo?.message}
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Group’s main business activities">
            <Controller
              control={control}
              name="mainActivities"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Textarea
                  name={name}
                  title=""
                  placeholder="Enter description..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['mainActivities']);
                  }}
                  className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                  error={errors?.mainActivities?.message}
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Source of wealth of the group (origin of total assets)">
            <Controller
              control={control}
              name="groupWealthSourceIds"
              defaultValue={[]}
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an options..."
                  list={
                    sourceOfWealth?.map((item) => ({
                      name: item.value,
                      value: item.id,
                    })) || []
                  }
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['groupWealthSourceIds']);
                  }}
                  selectWrapperClassName={
                    status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
                  }
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.groupWealthSourceIds?.message}
                  mode={SelectType.MULTIPLE}
                  fullWidth
                  borderless
                />
              )}
            />
          </RegulatedCompanyRow>
          {fields?.groupWealthSourceIds?.find((item) =>
            sourceOfWealth
              ?.find((client) => item === client.id)
              ?.value?.includes('Other'),
          ) && (
            <RegulatedCompanyRow title="Provide details">
              <Controller
                control={control}
                name="groupWealthSourceOther"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Textarea
                    name={name}
                    title=""
                    placeholder="Enter details..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['groupWealthSourceOther']);
                    }}
                    className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                    error={errors?.groupWealthSourceOther?.message}
                    disabled={status === CustomerStatus.CLOSED}
                    tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  />
                )}
              />
            </RegulatedCompanyRow>
          )}
          <RegulatedCompanyRow
            title={`Estimated total wealth of the group (in ${baseCurrency?.name})`}
          >
            <Controller
              control={control}
              name="estimatedTotalWealthEuro"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter amount..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['estimatedTotalWealthEuro']);
                  }}
                  className="borderless"
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.estimatedTotalWealthEuro?.message}
                  currency={baseCurrency}
                  amountInput
                />
              )}
            />
          </RegulatedCompanyRow>
        </>
      )}
    </>
  </AdditionalInfoBlock>
);

export default GroupOfCompanies;
